import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { getStore } from "../store/instance";
import actions from "actions";
import { hasSessionCookie } from "./sessionCookie";
import { mockRequestHandler } from "../../__mocks__/mockRequestHandler";

const useMockedRoutes = process.env.USE_MOCKED_ROUTES === "true";

const API_PROTECTED_BASE_URL = process.env.API_PROTECTED_BASE_URL;
const CSRF_COOKIE_NAME = process.env.CSRF_COOKIE_NAME;

/** Raised by the request interceptor to abort a request before it is sent. */
class NoSessionError extends Error {
  constructor() {
    super("No session cookie present; aborting api-protected request.");
    this.name = "NoSessionError";
  }
}

/**
 * Minimal typed subset of AxiosInstance used by API modules. Keeping
 * `getRequest()` return type concrete and generic avoids the union
 * `AxiosInstance | MockHandlerInterface` that breaks generic call-sites like
 * `Request.get<Goal[]>(url)`.
 */
interface ApiClient {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
}

/**
 * Axios-based HTTP client for the session-authenticated API-protected gateway.
 * Automatically attaches the CSRF token from the cookie named by CSRF_COOKIE_NAME
 * (expected value: "an_session_csrf") and handles 401 responses by dispatching
 * a Redux sign-out action.
 *
 * Both CSRF_COOKIE_NAME and API_PROTECTED_BASE_URL are baked in at Vite build time —
 * updating them requires a new build + deploy; changing runtime config has no effect.
 */
class ApiProtectedRequest {
  private request!: AxiosInstance;

  constructor() {
    if (useMockedRoutes) return;

    if (!API_PROTECTED_BASE_URL || !CSRF_COOKIE_NAME) {
      throw new Error(
        "API_PROTECTED_BASE_URL or CSRF_COOKIE_NAME is not configured. Check your .env file."
      );
    }

    this.request = axios.create({
      headers: { "Content-Type": "application/json" },
      baseURL: API_PROTECTED_BASE_URL,
      xsrfCookieName: CSRF_COOKIE_NAME,
      xsrfHeaderName: "x-csrf-token",
      withCredentials: true,
    });

    this.request.interceptors.request.use(
      (config) => {
        // Don't fire api-protected requests when there's no session cookie —
        // they'd only 401. Sign out immediately so the app redirects to the
        // auth portal instead of showing a loading state until the request
        // round-trips. (The session cookie is httpOnly; hasSessionCookie()
        // reads the non-httpOnly CSRF cookie that tracks it.)
        if (!hasSessionCookie()) {
          const store = getStore();
          if (store) {
            store.dispatch(actions.signOut());
          }
          return Promise.reject(new NoSessionError());
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.request.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const store = getStore();
          if (store) {
            store.dispatch(actions.signOut());
          }
        }
        return Promise.reject(error);
      }
    );
  }

  getRequest(): ApiClient {
    if (useMockedRoutes) {
      return {
        get: <T>(url: string, config?: AxiosRequestConfig) =>
          this.handleRequestWithFallback<T>(url, "GET", config),
        post: <T>(url: string, data?: unknown) =>
          this.handleRequestWithFallback<T>(url, "POST", { data }),
        put: <T>(url: string, data?: unknown) =>
          this.handleRequestWithFallback<T>(url, "PUT", { data }),
        delete: <T>(url: string, config?: AxiosRequestConfig) =>
          this.handleRequestWithFallback<T>(url, "DELETE", config),
        patch: <T>(url: string, data?: unknown) =>
          this.handleRequestWithFallback<T>(url, "PATCH", { data }),
      };
    }
    return this.request;
  }

  private async handleRequestWithFallback<T = unknown>(
    url: string,
    method: string,
    options: { data?: unknown; params?: unknown } = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const requestData = { query: options?.params, body: options?.data };
      const { status, response } = mockRequestHandler.resolve(url, method, requestData);
      return Promise.resolve({ status, data: response } as AxiosResponse<T>);
    } catch {
      console.warn(`Mock not found for ${method} ${url}. Falling back to live API.`);
      // Axios `method` expects a union type; cast to any to avoid typing friction
      // here since this method is only used for mocked fallback resolution in tests.
      return this.request.request<T>({
        method: method as any,
        url,
        data: options.data,
        params: options.params,
      }) as Promise<AxiosResponse<T>>;
    }
  }
}

export default ApiProtectedRequest;
